import React from 'react';
import { NodeProps, useReactFlow } from 'reactflow';
import axios from 'axios';
import { TextInput } from '../../../../form/textInput';
import { Select } from '../../../../form/select';
import { getvaluesUsingVariables, updateNodeValue } from '../../../../../services/method';
import { operationType, selectDataType } from '../../../../../services/types';
import { Textarea } from '../../../../form/textarea';
import { useDispatch } from 'react-redux';
import { reduxUpdateError } from '../../../../../redux/reducers/error';
import { Checkboxs } from '../../../../form/checkboxs';
import { Main, Styled } from './style';
import { Switch } from '../../../../form/switch';
import { AuthenticateSelect } from '../../../../auth/select';
import { getJiraAccountToken } from '../../../../auth/method';
import { SettingsBlock, settingsDataType } from '../../../../settingsBlock';
import { IoCheckmarkCircleOutline, IoWarningOutline } from 'react-icons/io5';
import { ExpandedBlock } from '../../../expanded';

type JiraProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

export function Jira({ id, data, setChildMinimize, setChildSettings }: JiraProps) {
   const dispatch = useDispatch();
   const [requests, setRequests] = React.useState<selectDataType[]>([
      { id: 1, name: 'Fetch Attachments', refId: 'fetch_attachments', isActive: true }, { id: 2, name: 'Create Issue', refId: 'create_issue' }, 
      { id: 3, name: 'Update Issue', refId: 'update_issue' }, { id: 4, name: 'Watch Issues', refId: 'watch_issue' },
      { id: 5, name: 'Let AI Agent Decide', refId: 'agent_decide' }
   ]);
   const [issues, setIssues] = React.useState<selectDataType[]>([{ id: 1, name: 'Task', isActive: true }, { id: 2, name: 'Bug' }, { id: 3, name: 'Story' }]);
   const { getNodes, setNodes } = useReactFlow();
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
   
   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // ---
   React.useEffect(() => {
      if (!data.value) {
         if (data.currentCategory === 'Actions') {
            setRequests(requests => requests.map(request => ({ ...request, isActive: request.refId === 'update_issue' })));
            setNodes(nodes => nodes.map(node => {
               if (node.id === id) {
                  return { ...node, data: { ...node.data, value: { ...node.data.value, request: 'update_issue' }}}
               } else return node;
            }));
         } else {
            updateNodeValue(id, setNodes, { request:  requests[0].refId, issue_type: issues[0].name  });
         }
      } else {
         setRequests(requests => requests.map(request => ({ ...request, isActive: request.refId === data.value.request })));
         setIssues(issues => issues.map(issue => ({ ...issue, isActive: issue.name === data.value.issue_type })));
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // ---
   const handleAuthenticate = async (type: 'add' | 'remove', data: { id: string, token: string, cloudId: string, domain: string }) => {
      if (type === 'add') { 
         console.log(data);
         // const cloudId = (await axios.get(`/project_management/Jira/get_cloud_id?domain=${data.domain}`, { headers: { Authorization: `Bearer ${data.token}`}})).data.cloud_id;
         // await axios.post(`/project_management/Jira/set_cloud_id/${data.cloudId}`, { headers: { Authorization: `Bearer ${data.token}`}});
         updateNodeValue(id, setNodes, { connection: data });
      } else if (type === 'remove') {
         updateNodeValue(id, setNodes, { connection: null });
      }
   }

   // ---
   const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string) => {
      if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: e.target.value });
   }

   // ---
   const handleSelectRequests = (action: 'request' | 'issue', selectId: number) => {
      if (action === 'request') {
         const refId = requests.find(obj => obj.id === selectId)?.refId;

         setRequests((requests) => requests.map((request) => {
            if (request.id === selectId) return { ...request, isActive: true };
            else return { ...request, isActive: false };
         }));
         setNodes(nodes => nodes.map(node => {
            if (node.id === id) {
               return { ...node, data: { ...node.data, value: { ...node.data.value, request: refId }}}
            } else return node;
         }));

      } else if (action === 'issue') {
         setIssues((issues) => issues.map((issue) => {
            if (issue.id === selectId) return { ...issue, isActive: true };
            else return { ...issue, isActive: false };
         }));
         updateNodeValue(id, setNodes, { issue_type: issues.find(i => i.id === selectId)?.name });
      }
   }

   // ---
   const handleButtons = async (type: 'attachments' | 'download' | 'listIssues') => {
      try {
         updateNodeValue(id, setNodes, { isLoading: true });
         const token = await getJiraAccountToken(data.value?.connection, id, setNodes);
         const credential = { headers: { Authorization: `Bearer ${token}`, 'X-Cloud-ID': data.value?.connection?.cloudId }};

         let general = { project_key: data.value.project_key };
         // Variables system
         general = getvaluesUsingVariables(general, getNodes()) as any;

         if (type === 'attachments') {
            const response = (await axios.get(`project_management/Jira/fetch_attachments?project_key=${general.project_key}`, credential)).data;
            updateNodeValue(id, setNodes, { isLoading: false, attachments: response.attachments.map((file: any) => ({ id: file.id, name: file.filename, type: file.mime_type })) });
   
         } else if (type === 'download') {
            const filter = data.value.attachments.filter((file: any) => file.isActive && !file.url).map((file: any) => file.id);
      
            if (filter.length) {
               const files: { id: number, url: string }[] = [];
               
               for (const id of filter) {
                  const url = (await axios.post(`/project_management/Jira/upload_attachment?attachment_id=${id}`, {}, credential)).data.upload_url;
                  files.push({ id, url });
               }
               const reMap = data.value.attachments.map((file: any) => ({ ...file, url: file.url || files.find((f) => f.id === file.id)?.url || '' }))
               updateNodeValue(id, setNodes, { isLoading: false, attachments: reMap });
            } else {
               updateNodeValue(id, setNodes, { isLoading: false });
            }
         } else if (type === 'listIssues') {
            if (!data.value.project_key) return dispatch(reduxUpdateError(`(${data.index}) project key field must be filled in.`));
            const fetch = (await axios.get(`/project_management/Jira/list_issues?project_key=${general.project_key}`, credential)).data.issues;
            updateNodeValue(id, setNodes, { list_issues: fetch.map((item: any, index: number) => ({ id: index+1, name: item.key })), isLoading: false });
         }
      } catch {
         updateNodeValue(id, setNodes, { isLoading: false });
      }
   }

   // ---
   const handleCheckboxs = async (type: 'list_issues', selectId: any, value?: boolean) => { 
      if (type === 'list_issues') {
         const refId = data.value.list_issues.find((issue: any) => issue.id === selectId).name;
         updateNodeValue(id, setNodes, { issue_key: refId, list_issues: data.value.list_issues.map((item: any) => ({ ...item, isActive: item.id === selectId ? !value : false }))});
      }
   }

   // ---
   const attachmentsLengthActive = data.value?.request === 'fetch_attachments' ? data.value?.attachments?.filter((item: any) => item.isActive)?.length : 0;
   const isWarning = !data.value?.connection || !data.value?.project_key;

   return (<>
      <SettingsBlock nodeId={ id }data={ settings } setData={ setSettings } />
      <Main>
         {isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
         </> : null}
         { isExpanded ? <ExpandedBlock
            data={{ id, ...data }} 
            handleClose={ () => setIsExpanded(false) } 
            isWarning={ isWarning }
            // styleParent={ (data.value?.files?.length ? { right: '-101.5px' } : {}) }
            style={{ minWidth: '420px' }}
         ><Styled>
            <AuthenticateSelect type="jira" defaultId={ data.value?.connection?.id } handleAuthenticate={ handleAuthenticate } background={ data.background} />
            <div className="action">
               { data.value?.connection && data.value?.request === 'update_issue' ? 
                  <button className="btn1 withBorder" onClick={ _=> handleButtons('listIssues') } disabled={ data.value?.isLoading }>Get List Issues</button> 
               : null }
               { data.value?.connection && data.value?.request === 'fetch_attachments' ? <>
                  <button className="btn1 withBorder" onClick={ _=> handleButtons('attachments') } disabled={ data.value?.isLoading }>Fetch</button> 
                  { attachmentsLengthActive ? <button className="btn1 withBorder download" onClick={ _=> handleButtons('download') } disabled={ data.value?.isLoading }>Download</button> : null }
               </> : null }
            </div>
            <Select title="Request" data={ requests } handleSelectItem={ (id) => handleSelectRequests('request', id) } isSmall={ true } />
            <TextInput keyRef="project_key" value={ data.value?.project_key } title="Project key" placeholder="project_key" handleChange={ handleChangeInput } />
            { data.value?.request === 'create_issue' ? <Select title="Issue Type" data={ issues } handleSelectItem={ (id) => handleSelectRequests('issue', id) } isSmall={ true } /> : null }
            { data.value?.request === 'update_issue' ? <TextInput keyRef="issue_key" value={ data.value?.issue_key } title="Issue Key" placeholder="issue_key" handleChange={ handleChangeInput } /> : null }
            { !['fetch_attachments', 'watch_issue'].includes(data.value?.request) ? <>
               <TextInput keyRef="summary" value={ data.value?.summary } title="Summary" placeholder="summary_text" handleChange={ handleChangeInput } />
               <Textarea keyRef="description" value={ data.value?.description } placeholder="description" handleChange={ handleChangeInput } />
            </> : null }
            { data.value?.request === 'update_issue' && data.value?.list_issues?.length ? <div className="filesEdit issues">
               <h3 className="title" style={{ backgroundColor: data.background, color: '#fff' }}>Issues ({ data.value?.list_issues?.length })</h3>
               <div className="scroll nowheel">
                  <Checkboxs data={ data.value?.list_issues || [] } handelCheckbox={ (selectId, value) => handleCheckboxs('list_issues', selectId, value) } />
               </div>
            </div> : null}
            { data.value?.request === 'fetch_attachments' ? <>
               { data.value?.attachments?.length ? <div className="filesEdit attachments">
                  <h3 className="title" style={{ backgroundColor: data.background, color: '#fff' }}>Attachments ({ attachmentsLengthActive }/{ data.value?.attachments?.length })</h3>
                  <div className="scroll nowheel">
                     <Checkboxs 
                        data={ data.value.attachments.map((file: any) => ({ ...file, isNotActive: !file.url })) } 
                        handelCheckbox={ (fileId: any, value?: boolean) => { 
                           updateNodeValue(id, setNodes, { attachments: data.value.attachments.map((item: any) => {
                              if (item.id === fileId) return ({ ...item, isActive: !value })
                              else return item;
                           })});
                        }}
                     />
                  </div>
               </div> : null }
               <Switch title="Start autopilot (with loop)" className="autopilot" value={ !!data.value?.isAutopilot } setValue={ () => updateNodeValue(id, setNodes, { isAutopilot: !data.value?.isAutopilot }) } />
               <p>Please select your keywords carefully to avoid downloading incorrect or unintended files!</p>
            </> : null }
         </Styled></ExpandedBlock> : null}
      </Main>
   </>);
}